import {Component, computed, inject, input, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslocoPipe} from '@jsverse/transloco';
import {rxResource} from '@angular/core/rxjs-interop';
import {EMPTY} from 'rxjs';
import {
  ProviderResultsSummaryControllerService,
  ProviderResultsSummaryRequest,
  ProviderResultsSummaryResponse,
} from '@frontend/shared-api';
import {LoadingComponent} from '../loading/loading.component';
import {LoadingSize} from '../loading/loading.types';
import {AiGeneratedLabelComponent} from '../ai-generated-label/ai-generated-label.component';

@Component({
  selector: 'app-provider-results-ai-summary',
  imports: [CommonModule, TranslocoPipe, LoadingComponent, AiGeneratedLabelComponent],
  templateUrl: './provider-results-ai-summary.component.html',
})
export class ProviderResultsAiSummaryComponent {
  protected readonly LoadingSize = LoadingSize;

  private readonly summaryController = inject(ProviderResultsSummaryControllerService);

  readonly projectId = input.required<number>();
  readonly request = input<ProviderResultsSummaryRequest | null>(null);

  // Keyed on a stable primitive so the (slow) LLM call fires once per
  // project/phase and not on every table recompute. The loader reads the latest
  // request payload directly.
  private readonly summaryResource = rxResource({
    params: () => {
      const request = this.request();
      if (!request || request.providers.length === 0) {
        return undefined;
      }
      return `${this.projectId()}:${request.phase}`;
    },
    stream: () => {
      const request = this.request();
      if (!request) {
        return EMPTY;
      }
      return this.summaryController.generateSummary({
        projectId: this.projectId(),
        providerResultsSummaryRequest: request,
      });
    },
  });

  readonly loading = computed(() => this.summaryResource.isLoading());
  readonly summary = computed<ProviderResultsSummaryResponse | undefined>(() =>
    this.summaryResource.value(),
  );
  readonly hasError = computed(() => !!this.summaryResource.error());
  readonly expandedProviderIds = signal<Set<string>>(new Set());

  toggleProvider(providerId: string): void {
    const next = new Set(this.expandedProviderIds());
    if (next.has(providerId)) {
      next.delete(providerId);
    } else {
      next.add(providerId);
    }
    this.expandedProviderIds.set(next);
  }

  isProviderExpanded(providerId: string): boolean {
    return this.expandedProviderIds().has(providerId);
  }

  retry(): void {
    this.summaryResource.reload();
  }
}
